/**
 * Logic thuần của Xưởng ảnh — tách khỏi file import antd để unit test chạy được
 * (antd v6 + react-router v7 không load nổi dưới jest của react-scripts).
 */
import type { FilmImageVariant, ImagePromptPreview } from '../../types/film';

export interface SourceBadge {
  text: string;
  /** Màu tag antd; 'default' cho trạng thái bình thường. */
  color: 'green' | 'orange' | 'blue' | 'default';
  hint: string;
}

/** Nhãn giải thích prompt đang dùng đến từ đâu — người dùng phải biết vì sao ô prompt lại ra nội dung đó. */
export function sourceBadge(p: Pick<ImagePromptPreview, 'source' | 'pinDropped'>): SourceBadge {
  if (p.pinDropped) {
    return {
      text: 'Description changed — pin dropped',
      color: 'orange',
      hint: 'You had pinned a prompt, but the outfit description was edited afterwards, so the prompt was rebuilt from the new description.',
    };
  }
  if (p.source === 'PINNED') {
    return { text: 'Using the pinned prompt', color: 'green', hint: 'The prompt of the variant you picked. Every later generation uses it until you edit the description.' };
  }
  if (p.source === 'OVERRIDE') {
    return { text: 'Your edited prompt', color: 'blue', hint: 'Not pinned yet — hit Use on the variant you like to pin it.' };
  }
  return { text: 'Built from the description', color: 'default', hint: 'No prompt pinned; this text is built from the template and the current description.' };
}

/** Có gửi prompt sửa tay lên không: chỉ khi người dùng thực sự đổi nội dung. */
export function overrideOf(current: string, original: string): string | undefined {
  const c = String(current || '').trim();
  const o = String(original || '').trim();
  return c && c !== o ? c : undefined;
}

/** Bản đang dùng luôn đứng đầu; còn lại giữ nguyên thứ tự mới-nhất-trước từ API. */
export function orderVariants(list: FilmImageVariant[]): FilmImageVariant[] {
  const chosen = list.filter((v) => v.isChosen);
  return [...chosen, ...list.filter((v) => !v.isChosen)];
}

/** Vì sao không xoá được — trả chuỗi lý do, hoặc null nếu xoá được. */
export function removeBlockedReason(v: FilmImageVariant): string | null {
  return v.isChosen ? 'This is the variant in use — pick another one first' : null;
}

/**
 * Nhãn ngắn cho một bản: model + thời điểm.
 *
 * Bản do luồng tự động sinh không có model (nó được kéo vào danh sách từ cột ảnh đang dùng của bank) —
 * gọi thẳng là "original" để người dùng biết đó là ảnh có sẵn trước khi họ mở Xưởng ảnh, và biết đường
 * quay lại nó sau khi thử bản mới.
 */
export function variantCaption(v: FilmImageVariant): string {
  if (!v.model) return ['original', captionTime(v)].filter(Boolean).join(' · ');
  const model = (v.model || '').replace(/^gemini-/, '').replace(/-preview$/, '');
  return [model, captionTime(v)].filter(Boolean).join(' · ');
}

function captionTime(v: FilmImageVariant): string {
  const t = v.createdAt ? new Date(v.createdAt) : null;
  return t ? `${String(t.getHours()).padStart(2, '0')}:${String(t.getMinutes()).padStart(2, '0')}` : '';
}

/**
 * Nhãn cho ô chọn model: CHỈ tên.
 *
 * Từng gắn kèm giá và ELO, nhưng chuỗi dài tới mức ô chọn cắt cụt ngay ở chữ ELO — thông tin thêm vào
 * lại đúng phần bị mất, còn tên model thì bị đẩy chật. Giá/ELO nay không hiện trên UI nữa.
 */
export function modelOptionLabel(m: { label: string }): string {
  return m.label;
}

/**
 * Cảnh báo hiện dưới ô chọn; null nếu model không có gì đáng lưu ý.
 * 260922 E2-P3: bỏ câu "Expensive model — costs about 3×" — User chốt màn Bank không nói tới giá (*"bỏ hết giá"*).
 */
export function modelWarning(m?: { strictModeration?: boolean }): string | null {
  if (!m) return null;
  if (m.strictModeration) return 'This provider moderates hard — measured: 10% of descriptions blocked, usually bare-shoulder outfits. If blocked, switch model and generate again.';
  return null;
}
